import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/adminlayout';
import { apiGet, apiPost, apiPut, apiDelete } from '../../utils/api';
import { PRODUCTION_URL } from '../productionLink/productionLink';

const BanquetSetup = () => {
  const [loading, setLoading] = useState(true);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [activeTab, setActiveTab] = useState('halls');
  const [halls, setHalls] = useState([]);
  const [packages, setPackages] = useState([]);
  const [catering, setCatering] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [successMessage, setSuccessMessage] = useState('');

  const colors = {
    primary: '#2D5016',
    accent: '#6ba83e',
    lightGray: '#f8faf9',
    mediumGray: '#e8ede9',
    textDark: '#1a1a1a',
    textLight: '#6b7280',
    white: '#ffffff',
    success: '#4a7c2a',
    warning: '#f59e0b',
    danger: '#ef4444'
  };

  const isMobile = windowWidth <= 768;
  const isTablet = windowWidth > 768 && windowWidth <= 1024;

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchAllData();
  }, []);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const fetchAllData = async () => {
    setLoading(true);
    await Promise.all([fetchHalls(), fetchPackages(), fetchCatering()]);
    setLoading(false);
  };

  const fetchHalls = async () => {
    try {
      const data = await apiGet('/admin/banquet/setup/halls');
      if (data.success) setHalls(data.data);
    } catch (error) {
      console.error('Error fetching halls:', error);
    }
  };

  const fetchPackages = async () => {
    try {
      const data = await apiGet('/admin/banquet/setup/packages');
      if (data.success) setPackages(data.data);
    } catch (error) {
      console.error('Error fetching packages:', error);
    }
  };

  const fetchCatering = async () => {
    try {
      const data = await apiGet('/admin/banquet/setup/catering');
      if (data.success) setCatering(data.data);
    } catch (error) {
      console.error('Error fetching catering items:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const endpoint = modalType === 'hall' ? 'halls' : modalType === 'package' ? 'packages' : 'catering';

    try {
      const url = editingItem
        ? `${PRODUCTION_URL}/api/admin/banquet/setup/${endpoint}/${editingItem.id}`
        : `${PRODUCTION_URL}/api/admin/banquet/setup/${endpoint}`;

      const response = await fetch(url, {
        method: editingItem ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      if (data.success) {
        setSuccessMessage(`✅ ${modalType} ${editingItem ? 'updated' : 'created'} successfully!`);
        setShowModal(false);
        setEditingItem(null);
        setFormData({});
        fetchAllData();
      }
    } catch (error) {
      console.error('Error saving:', error);
    }
  };

  const handleEdit = (type, item) => {
    setModalType(type);
    setEditingItem(item);
    setFormData(item);
    setShowModal(true);
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    const endpoint = type === 'hall' ? 'halls' : type === 'package' ? 'packages' : 'catering';

    try {
      const response = await fetch(`${PRODUCTION_URL}/api/admin/banquet/setup/${endpoint}/${id}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (data.success) {
        setSuccessMessage('✅ Item deleted successfully!');
        fetchAllData();
      }
    } catch (error) {
      console.error('Error deleting:', error);
    }
  };

  const openModal = (type) => {
    setModalType(type);
    setEditingItem(null);
    setFormData(getDefaultFormData(type));
    setShowModal(true);
  };

  const getDefaultFormData = (type) => {
    switch (type) {
      case 'hall':
        return { hall_name: '', capacity: '', area_sqft: '', description: '', base_price: '', amenities: '', is_available: true };
      case 'package':
        return { package_name: '', package_type: 'wedding', description: '', price_per_person: '', minimum_guests: 50, includes: '', is_active: true };
      case 'catering':
        return { item_name: '', category: 'main_course', cuisine_type: 'Indian', price_per_unit: '', unit_type: 'per_person', description: '', is_veg: true, is_available: true };
      default:
        return {};
    }
  };

  const formatCurrency = (amount) => {
    return `₹${parseFloat(amount || 0).toLocaleString('en-IN')}`;
  };

  const styles = {
    pageHeader: {
      marginBottom: isMobile ? '16px' : '24px',
      padding: isMobile ? '0 4px' : '0'
    },
    pageTitle: {
      fontSize: isMobile ? '20px' : isTablet ? '24px' : '28px',
      fontWeight: '700',
      color: colors.textDark,
      marginBottom: '6px'
    },
    tabContainer: {
      display: 'flex',
      gap: '8px',
      marginBottom: isMobile ? '16px' : '20px',
      padding: isMobile ? '0 4px' : '0',
      overflowX: 'auto'
    },
    tab: {
      padding: isMobile ? '10px 16px' : '12px 20px',
      borderRadius: '8px',
      border: `1px solid ${colors.mediumGray}`,
      background: colors.white,
      fontSize: isMobile ? '13px' : '14px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      whiteSpace: 'nowrap'
    },
    activeTab: {
      background: colors.primary,
      color: colors.white,
      borderColor: colors.primary
    },
    contentCard: {
      background: colors.white,
      padding: isMobile ? '16px' : '24px',
      borderRadius: isMobile ? '14px' : '16px',
      boxShadow: '0 8px 24px rgba(45, 80, 22, 0.08)',
      border: `1px solid ${colors.mediumGray}`,
      margin: isMobile ? '0 4px' : '0'
    },
    sectionTitle: {
      fontSize: isMobile ? '16px' : '20px',
      fontWeight: '700',
      color: colors.textDark,
      marginBottom: isMobile ? '12px' : '16px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      justifyContent: 'space-between'
    },
    button: {
      padding: isMobile ? '8px 14px' : '10px 18px',
      borderRadius: '6px',
      border: 'none',
      fontSize: isMobile ? '12px' : '13px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: colors.primary,
      color: colors.white
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: isMobile ? '12px' : '13px'
    },
    tableHeader: {
      background: colors.lightGray,
      fontWeight: '700',
      textAlign: 'left',
      padding: isMobile ? '10px' : '12px',
      borderBottom: `2px solid ${colors.mediumGray}`
    },
    tableCell: {
      padding: isMobile ? '10px' : '12px',
      borderBottom: `1px solid ${colors.lightGray}`
    },
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: isMobile ? '20px' : '40px'
    },
    modalContent: {
      background: colors.white,
      borderRadius: isMobile ? '16px' : '20px',
      padding: isMobile ? '20px' : '32px',
      maxWidth: isMobile ? '100%' : '600px',
      width: '100%',
      maxHeight: '90vh',
      overflowY: 'auto'
    },
    formGroup: {
      marginBottom: '16px'
    },
    label: {
      display: 'block',
      marginBottom: '6px',
      fontSize: '12px',
      fontWeight: '600',
      color: colors.textDark
    },
    input: {
      width: '100%',
      padding: '10px 12px',
      borderRadius: '8px',
      border: `1px solid ${colors.mediumGray}`,
      fontSize: '13px',
      fontFamily: 'inherit'
    },
    select: {
      width: '100%',
      padding: '10px 12px',
      borderRadius: '8px',
      border: `1px solid ${colors.mediumGray}`,
      fontSize: '13px',
      fontFamily: 'inherit',
      background: colors.white
    },
    actionButton: {
      padding: '6px 12px',
      borderRadius: '6px',
      border: 'none',
      fontSize: '11px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      marginRight: '6px'
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
            <div>Loading setup...</div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {successMessage && (
        <div style={{
          padding: isMobile ? '12px 16px' : '14px 20px',
          borderRadius: '10px',
          marginBottom: '16px',
          fontSize: isMobile ? '13px' : '14px',
          fontWeight: '600',
          background: 'rgba(74, 124, 42, 0.15)',
          color: colors.success,
          border: `2px solid ${colors.success}`,
          margin: isMobile ? '0 4px 12px 4px' : '0 0 16px 0'
        }}>
          {successMessage}
        </div>
      )}

      <div style={styles.pageHeader}>
        <h1 style={styles.pageTitle}>Banquet Setup</h1>
        <p style={{ fontSize: isMobile ? '12px' : '14px', color: colors.textLight }}>
          {isMobile ? 'Halls & menus' : 'Configure banquet halls, menus, and event packages'}
        </p>
      </div>

      {/* Tabs */}
      <div style={styles.tabContainer}>
        <button
          onClick={() => setActiveTab('halls')}
          style={{ ...styles.tab, ...(activeTab === 'halls' ? styles.activeTab : {}) }}
        >
          🏛️ Banquet Halls
        </button>
        <button
          onClick={() => setActiveTab('packages')}
          style={{ ...styles.tab, ...(activeTab === 'packages' ? styles.activeTab : {}) }}
        >
          📦 Event Packages
        </button>
        <button
          onClick={() => setActiveTab('catering')}
          style={{ ...styles.tab, ...(activeTab === 'catering' ? styles.activeTab : {}) }}
        >
          🍽️ Catering Menu
        </button>
      </div>

      {/* Banquet Halls Tab */}
      {activeTab === 'halls' && (
        <div style={styles.contentCard}>
          <div style={styles.sectionTitle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>🏛️</span>
              <span>Banquet Halls</span>
            </div>
            <button onClick={() => openModal('hall')} style={styles.button}>
              + Add Hall
            </button>
          </div>

          {halls.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '30px', color: colors.textLight }}>
              No halls found
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.tableHeader}>Hall Name</th>
                    <th style={styles.tableHeader}>Capacity</th>
                    {!isMobile && <th style={styles.tableHeader}>Area (sqft)</th>}
                    <th style={styles.tableHeader}>Base Price</th>
                    <th style={styles.tableHeader}>Status</th>
                    <th style={styles.tableHeader}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {halls.map(hall => (
                    <tr key={hall.id}>
                      <td style={styles.tableCell}><strong>{hall.hall_name}</strong></td>
                      <td style={styles.tableCell}>{hall.capacity} guests</td>
                      {!isMobile && <td style={styles.tableCell}>{hall.area_sqft}</td>}
                      <td style={styles.tableCell}><strong>{formatCurrency(hall.base_price)}</strong></td>
                      <td style={styles.tableCell}>
                        <span style={{
                          padding: '4px 10px',
                          borderRadius: '6px',
                          fontSize: '11px',
                          fontWeight: '600',
                          background: hall.is_available ? `${colors.success}20` : `${colors.danger}20`,
                          color: hall.is_available ? colors.success : colors.danger
                        }}>
                          {hall.is_available ? 'Available' : 'Unavailable'}
                        </span>
                      </td>
                      <td style={styles.tableCell}>
                        <button
                          onClick={() => handleEdit('hall', hall)}
                          style={{ ...styles.actionButton, background: colors.accent, color: colors.white }}
                        >
                          ✏️ Edit
                        </button>
                        <button
                          onClick={() => handleDelete('hall', hall.id)}
                          style={{ ...styles.actionButton, background: colors.danger, color: colors.white }}
                        >
                          🗑️ Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Event Packages Tab */}
      {activeTab === 'packages' && (
        <div style={styles.contentCard}>
          <div style={styles.sectionTitle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>📦</span>
              <span>Event Packages</span>
            </div>
            <button onClick={() => openModal('package')} style={styles.button}>
              + Add Package
            </button>
          </div>

          {packages.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '30px', color: colors.textLight }}>
              No packages found
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)', gap: '16px' }}>
              {packages.map(pkg => (
                <div
                  key={pkg.id}
                  style={{
                    padding: isMobile ? '16px' : '20px',
                    background: colors.lightGray,
                    borderRadius: '12px',
                    border: `1px solid ${colors.mediumGray}`
                  }}
                >
                  <div style={{ fontSize: isMobile ? '16px' : '18px', fontWeight: '700', marginBottom: '8px', color: colors.primary }}>
                    {pkg.package_name}
                  </div>
                  <div style={{ fontSize: '12px', color: colors.textLight, marginBottom: '8px', textTransform: 'capitalize' }}>
                    {pkg.package_type.replace('_', ' ')}
                  </div>
                  <div style={{ fontSize: isMobile ? '20px' : '24px', fontWeight: '700', marginBottom: '8px', color: colors.success }}>
                    {formatCurrency(pkg.price_per_person)}/person
                  </div>
                  <div style={{ fontSize: '12px', color: colors.textLight, marginBottom: '12px' }}>
                    Min. {pkg.minimum_guests} guests
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => handleEdit('package', pkg)}
                      style={{ ...styles.actionButton, background: colors.accent, color: colors.white, flex: 1 }}
                    >
                      ✏️ Edit
                    </button>
                    <button
                      onClick={() => handleDelete('package', pkg.id)}
                      style={{ ...styles.actionButton, background: colors.danger, color: colors.white, flex: 1 }}
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Catering Menu Tab */}
      {activeTab === 'catering' && (
        <div style={styles.contentCard}>
          <div style={styles.sectionTitle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>🍽️</span>
              <span>Catering Menu</span>
            </div>
            <button onClick={() => openModal('catering')} style={styles.button}>
              + Add Item
            </button>
          </div>

          {catering.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '30px', color: colors.textLight }}>
              No catering items found
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.tableHeader}>Item Name</th>
                    <th style={styles.tableHeader}>Category</th>
                    {!isMobile && <th style={styles.tableHeader}>Cuisine</th>}
                    <th style={styles.tableHeader}>Price</th>
                    <th style={styles.tableHeader}>Type</th>
                    <th style={styles.tableHeader}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {catering.map(item => (
                    <tr key={item.id}>
                      <td style={styles.tableCell}><strong>{item.item_name}</strong></td>
                      <td style={{ ...styles.tableCell, textTransform: 'capitalize' }}>
                        {item.category.replace('_', ' ')}
                      </td>
                      {!isMobile && <td style={styles.tableCell}>{item.cuisine_type}</td>}
                      <td style={styles.tableCell}>
                        <strong>{formatCurrency(item.price_per_unit)}</strong>
                        <div style={{ fontSize: '10px', color: colors.textLight }}>
                          {item.unit_type.replace('_', ' ')}
                        </div>
                      </td>
                      <td style={styles.tableCell}>
                        {item.is_veg ? (
                          <span style={{ color: colors.success, fontSize: '11px' }}>🟢 Veg</span>
                        ) : (
                          <span style={{ color: colors.danger, fontSize: '11px' }}>🔴 Non-Veg</span>
                        )}
                      </td>
                      <td style={styles.tableCell}>
                        <button
                          onClick={() => handleEdit('catering', item)}
                          style={{ ...styles.actionButton, background: colors.accent, color: colors.white }}
                        >
                          ✏️ Edit
                        </button>
                        <button
                          onClick={() => handleDelete('catering', item.id)}
                          style={{ ...styles.actionButton, background: colors.danger, color: colors.white }}
                        >
                          🗑️
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Universal Modal */}
      {showModal && (
        <div style={styles.modal} onClick={() => setShowModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ fontSize: isMobile ? '18px' : '22px', marginBottom: '20px', fontWeight: '700' }}>
              {editingItem ? `Edit ${modalType}` : `Add New ${modalType}`}
            </h3>

            <form onSubmit={handleSubmit}>
              {/* Hall Form */}
              {modalType === 'hall' && (
                <>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Hall Name *</label>
                    <input
                      type="text"
                      value={formData.hall_name || ''}
                      onChange={(e) => setFormData({ ...formData, hall_name: e.target.value })}
                      style={styles.input}
                      required
                    />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '12px' }}>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Capacity *</label>
                      <input
                        type="number"
                        value={formData.capacity || ''}
                        onChange={(e) => setFormData({ ...formData, capacity: e.target.value })}
                        style={styles.input}
                        required
                      />
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Area (sqft)</label>
                      <input
                        type="number"
                        value={formData.area_sqft || ''}
                        onChange={(e) => setFormData({ ...formData, area_sqft: e.target.value })}
                        style={styles.input}
                      />
                    </div>
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Base Price (₹) *</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.base_price || ''}
                      onChange={(e) => setFormData({ ...formData, base_price: e.target.value })}
                      style={styles.input}
                      required
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Amenities</label>
                    <textarea
                      value={formData.amenities || ''}
                      onChange={(e) => setFormData({ ...formData, amenities: e.target.value })}
                      style={{ ...styles.input, minHeight: '80px' }}
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Description</label>
                    <textarea
                      value={formData.description || ''}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      style={{ ...styles.input, minHeight: '80px' }}
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Status</label>
                    <select
                      value={formData.is_available}
                      onChange={(e) => setFormData({ ...formData, is_available: e.target.value === 'true' })}
                      style={styles.select}
                    >
                      <option value="true">Available</option>
                      <option value="false">Unavailable</option>
                    </select>
                  </div>
                </>
              )}

              {/* Package Form */}
              {modalType === 'package' && (
                <>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Package Name *</label>
                    <input
                      type="text"
                      value={formData.package_name || ''}
                      onChange={(e) => setFormData({ ...formData, package_name: e.target.value })}
                      style={styles.input}
                      required
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Package Type *</label>
                    <select
                      value={formData.package_type || 'wedding'}
                      onChange={(e) => setFormData({ ...formData, package_type: e.target.value })}
                      style={styles.select}
                      required
                    >
                      <option value="wedding">Wedding</option>
                      <option value="corporate">Corporate</option>
                      <option value="birthday">Birthday</option>
                      <option value="conference">Conference</option>
                      <option value="custom">Custom</option>
                    </select>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '12px' }}>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Price Per Person (₹) *</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.price_per_person || ''}
                        onChange={(e) => setFormData({ ...formData, price_per_person: e.target.value })}
                        style={styles.input}
                        required
                      />
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Minimum Guests</label>
                      <input
                        type="number"
                        value={formData.minimum_guests || 50}
                        onChange={(e) => setFormData({ ...formData, minimum_guests: e.target.value })}
                        style={styles.input}
                      />
                    </div>
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Includes</label>
                    <textarea
                      value={formData.includes || ''}
                      onChange={(e) => setFormData({ ...formData, includes: e.target.value })}
                      style={{ ...styles.input, minHeight: '80px' }}
                      placeholder="e.g., Welcome Drink, 3-Course Meal, Decoration"
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Description</label>
                    <textarea
                      value={formData.description || ''}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      style={{ ...styles.input, minHeight: '80px' }}
                    />
                  </div>
                </>
              )}

              {/* Catering Form */}
              {modalType === 'catering' && (
                <>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Item Name *</label>
                    <input
                      type="text"
                      value={formData.item_name || ''}
                      onChange={(e) => setFormData({ ...formData, item_name: e.target.value })}
                      style={styles.input}
                      required
                    />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '12px' }}>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Category *</label>
                      <select
                        value={formData.category || 'main_course'}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                        style={styles.select}
                        required
                      >
                        <option value="appetizer">Appetizer</option>
                        <option value="main_course">Main Course</option>
                        <option value="dessert">Dessert</option>
                        <option value="beverage">Beverage</option>
                        <option value="special">Special</option>
                      </select>
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Cuisine Type</label>
                      <input
                        type="text"
                        value={formData.cuisine_type || ''}
                        onChange={(e) => setFormData({ ...formData, cuisine_type: e.target.value })}
                        style={styles.input}
                        placeholder="e.g., Indian, Chinese"
                      />
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '12px' }}>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Price (₹) *</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.price_per_unit || ''}
                        onChange={(e) => setFormData({ ...formData, price_per_unit: e.target.value })}
                        style={styles.input}
                        required
                      />
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Unit Type *</label>
                      <select
                        value={formData.unit_type || 'per_person'}
                        onChange={(e) => setFormData({ ...formData, unit_type: e.target.value })}
                        style={styles.select}
                        required
                      >
                        <option value="per_person">Per Person</option>
                        <option value="per_plate">Per Plate</option>
                        <option value="per_kg">Per Kg</option>
                        <option value="per_item">Per Item</option>
                      </select>
                    </div>
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Type *</label>
                    <select
                      value={formData.is_veg}
                      onChange={(e) => setFormData({ ...formData, is_veg: e.target.value === 'true' })}
                      style={styles.select}
                    >
                      <option value="true">🟢 Vegetarian</option>
                      <option value="false">🔴 Non-Vegetarian</option>
                    </select>
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Description</label>
                    <textarea
                      value={formData.description || ''}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      style={{ ...styles.input, minHeight: '80px' }}
                    />
                  </div>
                </>
              )}

              <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                <button type="submit" style={{ ...styles.button, flex: 1 }}>
                  {editingItem ? 'Update' : 'Create'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  style={{ ...styles.button, background: colors.mediumGray, color: colors.textDark, flex: 1 }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default BanquetSetup;